/**
 * The seam between "a request was refused because this sign-in is too old"
 * (`STALE_AUTH_REAUTH_REQUIRED`, from `requireRecentAuth(N)`) and "ask the
 * person to confirm it is them".
 *
 * The API reads `auth_time`, which moves only on a genuine sign-in or
 * re-authentication — never on a token refresh — so the interceptor's generic
 * refresh-and-retry cannot satisfy it. Until 2026-10-29 only `apps/admin` could
 * answer it (per mutation, `useDestructiveMutation`); every other app showed
 * "please confirm it's you" with nothing to confirm with, which made each
 * step-up route there a dead end (SOC 2 register R59). Mechanics shared with
 * the second-factor broker: `./stepUpBroker`.
 */
import { createStepUpBroker } from './stepUpBroker';

export interface RecentAuthRequest {
  /** The route's window, from the refusal's `details.maxAgeMinutes`, when it said. */
  maxAgeMinutes?: number;
}

/**
 * Resolves true once the person re-authenticated AND the session cookie was
 * re-minted with the new `auth_time` (`finalizeReauth`), false if they gave up.
 */
export type RecentAuthHandler = (request: RecentAuthRequest) => Promise<boolean>;

const broker = createStepUpBroker<[RecentAuthRequest]>('RecentAuthBroker');

/**
 * Register the UI that can re-authenticate. Returns an unregister function.
 * Mounted by `RecentAuthProvider`; with nothing registered `requestRecentAuth`
 * resolves FALSE and the refusal reaches the caller as before.
 */
export function registerRecentAuthHandler(fn: RecentAuthHandler): () => void {
  return broker.register(fn);
}

/** True when a surface can actually re-authenticate. */
export function canPromptForRecentAuth(): boolean {
  return broker.canPrompt();
}

/** Ask for a re-authentication, coalescing concurrent asks into one prompt. Never throws. */
export function requestRecentAuth(request: RecentAuthRequest): Promise<boolean> {
  return broker.request(request);
}

/** Test seam. Never call from application code. */
export function __resetRecentAuthBrokerForTests(): void {
  broker.reset();
}
